<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <base-crud
          :ref="'professionalsCrud'"
          :crudName="$t('professional.crud.title')"
          :newText="$t('professional.crud.newText')"
          :table="table"
          :columns="columns"
          :options="options"
          :endPoint="endPoint"
          :enableAdd="$hasAbility('profissional.gerenciar')"
          :enableEdit="$hasAbility('profissional.gerenciar')"
          :enableDelete="$hasAbility('profissional.gerenciar')"
          :enableView="false"
          :enableReset="false"
          :dataForm="dataForm"
          :extra="manageExtraButton"
          @manage="openManageModal"
        >
        </base-crud>
      </div>
    </div>

    <!-- Modal gerenciar: info do profissional + vínculos com nós -->
    <b-modal
      id="modal-manage-professional"
      size="lg"
      centered
      hide-footer
      hide-header
      body-class="p-0"
      content-class="manage-modal"
    >
      <div class="manage-modal__header">
        <span class="manage-modal__avatar">
          <feather-icon icon="UserIcon" size="20" />
        </span>
        <div class="manage-modal__heading">
          <h5 class="mb-0">{{ manageProfessional.name }}</h5>
          <small class="text-muted">{{ $t('professional.manage.subtitle') }}</small>
        </div>
        <button
          type="button"
          class="manage-modal__close"
          @click="$bvModal.hide('modal-manage-professional')"
        >
          <feather-icon icon="XIcon" size="18" />
        </button>
      </div>

      <div class="manage-modal__body">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <span class="manage-modal__section-title">{{ $t('professional.manage.linksLabel') }}</span>
          <b-button
            v-if="$hasAbility('node.admin')"
            size="sm"
            variant="primary"
            @click="openAddLink"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            {{ $t('professional.manage.addLinkButton') }}
          </b-button>
        </div>

        <div v-if="professionalLinks.length" class="link-list">
          <div v-for="link in professionalLinks" :key="link.id" class="link-row">
            <span class="link-row__icon">
              <feather-icon icon="GitBranchIcon" size="16" />
            </span>
            <div class="link-row__main">
              <div class="link-row__node">
                {{ link.node ? link.node.name : link.node_id }}
                <b-badge :variant="linkStatus(link).variant" class="ml-50">
                  {{ linkStatus(link).label }}
                </b-badge>
              </div>
              <div class="link-row__meta">
                <span v-if="link.role">{{ link.role.name }} · </span>
                {{ formatIsoDate(link.starts_at) }} →
                {{ link.ends_at ? formatIsoDate(link.ends_at) : $t('professional.manage.noEndDate') }}
              </div>
            </div>
            <div class="link-row__actions">
              <b-button
                v-if="$hasAbility('node.admin')"
                size="sm"
                variant="outline-primary"
                class="mr-50"
                :title="$t('professional.manage.editValidity')"
                @click="openEditLink(link)"
              >
                <feather-icon icon="CalendarIcon" size="14" />
              </b-button>
              <b-button
                v-if="$hasAbility('node.admin')"
                size="sm"
                variant="outline-danger"
                :title="$t('professional.manage.unlink')"
                @click="unlinkNode(link)"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </div>
          </div>
        </div>
        <div v-else class="p-3 text-center text-muted">
          {{ $t('professional.manage.noLinks') }}
        </div>
      </div>
    </b-modal>

    <!-- Modal adicionar vínculo -->
    <b-modal
      id="modal-add-link"
      centered
      :title="$t('professional.manage.addLinkModalTitle')"
      :ok-title="$t('professional.manage.linkOkTitle')"
      :cancel-title="$t('common.cancel')"
      @ok="saveLink"
    >
      <div class="form-group">
        <label>{{ $t('professional.manage.nodeLabel') }}</label>
        <v-select
          v-model="newLink.node_id"
          :options="availableNodes"
          :reduce="(n) => n.id"
          label="name"
          class="vselect"
          :placeholder="$t('professional.manage.selectNodePlaceholder')"
        />
      </div>
      <div class="form-group">
        <label>{{ $t('professional.manage.roleLabel') }}</label>
        <v-select
          v-model="newLink.role_id"
          :options="allLinkRoles"
          :reduce="(r) => r.id"
          label="name"
          class="vselect"
          :placeholder="$t('professional.manage.selectRolePlaceholder')"
        />
      </div>
      <div class="form-row">
        <div class="form-group col-6">
          <label><span class="required">*</span> {{ $t('professional.manage.startDateLabel') }}</label>
          <input v-model="newLink.starts_at" type="date" class="form-control" />
        </div>
        <div class="form-group col-6">
          <label>{{ $t('professional.manage.endDateLabel') }}
            <small class="text-muted">{{ $t('node.form.optionalHint') }}</small>:</label>
          <input
            v-model="newLink.ends_at"
            type="date"
            class="form-control"
            :min="newLink.starts_at"
          />
        </div>
      </div>
    </b-modal>

    <!-- Modal editar vigência -->
    <b-modal
      id="modal-edit-professional-link"
      centered
      :title="$t('professional.manage.editLinkModalTitle')"
      :ok-title="$t('common.save')"
      :cancel-title="$t('common.cancel')"
      @ok="saveEditLink"
    >
      <div class="form-row">
        <div class="form-group col-6">
          <label><span class="required">*</span> {{ $t('professional.manage.startDateLabel') }}</label>
          <input v-model="editingLink.starts_at" type="date" class="form-control" />
        </div>
        <div class="form-group col-6">
          <label>{{ $t('professional.manage.endDateLabel') }}
            <small class="text-muted">{{ $t('node.form.optionalHint') }}</small>:</label>
          <input
            v-model="editingLink.ends_at"
            type="date"
            class="form-control"
            :min="editingLink.starts_at"
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import BaseCrud from "../../layouts/Base/BaseCrud";
import vSelect from "vue-select";
import swal from "sweetalert";

export default {
  data: function () {
    return {
      table: "professionalsTable",
      showMessageImport: false,
      professionalsCrud: {},
      pages: [],
      url: "",
      manageProfessional: {},
      professionalLinks: [],
      allNodesForLinks: [],
      allLinkRoles: [],
      newLink: { node_id: null, role_id: null, starts_at: "", ends_at: "" },
      editingLink: { id: null, starts_at: "", ends_at: "" },
      columns: ["id", "name", "cbo_id", "status", "actions"],
      tableData: [
        "id",
        "cbo_id",
        "name",
        "requester_id",
        "cns",
        "council_number",
        "council_type",
        "sector",
        "phone",
        "requester_doctor",
        "status",
      ],
      options: {
        filterByColumn: true,
        debounce: 1000,
        filterable: ["id", "name", "cbo_id", "status"],
        sortable: ["id", "name", "requester_id"],
        sortIcon: {
          base: "fas",
          up: "fa-chevron-up",
          down: "fa-chevron-down",
          is: "fa-sort",
        },
        pagination: { chunk: 10, dropdown: false, nav: "scroll" },
        perPage: 10,
        perPageValues: [10, 25, 50, 100],
        headings: {
          id: this.$t('professional.crud.headings.id'),
          name: this.$t('professional.crud.headings.name'),
          cbo_id: this.$t('professional.crud.headings.cboId'),
          status: this.$t('common.status'),
          actions: this.$t('common.actions'),
        },

        texts: {
          filterBy: this.$t('professional.crud.filterBy'),
          defaultOption: this.$t('common.select'),
        },
        listColumns: {
          status: [
            {
              id: 1,
              text: this.$t('common.active'),
            },
            {
              id: 0,
              text: this.$t('common.inactive'),
            },
          ],
        },
        templates: {
          status: (h, row, index) => {
            return row.status == 1
              ? this.$t('common.active')
              : row.status == 0
              ? this.$t('common.inactive')
              : this.$t('common.inactive');
          },
          cbo_id: (h, row, index) => {
            return row.cbo ? row.cbo.cbo_name : this.$t('professional.crud.noName');
          },
        },
        requestFunction: function (data) {
          let requestData = {};

          console.log(this.$parent.$parent);

          let query = this.$parent.$parent.$parent.query(data.query);
          requestData.params = data;
          requestData.params.query = "";
          requestData.params.with = ["cbo"];

          return axios.get(this.url + "?" + query, requestData).catch(
            function (e) {
              this.dispatch("error", e);
            }.bind(this)
          );
        },
      },
      endPoint: "professionals",
      dataForm: {},
    };
  },
  components: {
    BaseCrud,
    vSelect,
  },
  computed: {
    manageExtraButton() {
      return [
        {
          id: "manage",
          name: "manage",
          title: this.$t("professional.crud.manageButton"),
          icon: "fas fa-cog",
          class: "btn btn-crud view",
          label: "",
        },
      ];
    },
    availableNodes() {
      // Vínculo encerrado (ends_at no passado) não conta como "vinculado" —
      // só um vínculo vigente tira o nó da lista de disponíveis.
      const today = this.todayIso();
      const linkedIds = this.professionalLinks
        .filter((l) => l.starts_at <= today && (!l.ends_at || l.ends_at >= today))
        .map((l) => l.node_id);
      return this.allNodesForLinks.filter((n) => !linkedIds.includes(n.id));
    },
  },
  methods: {
    openManageModal({ row }) {
      this.manageProfessional = row;
      this.professionalLinks = [];
      this.loadProfessionalLinks();
      this.loadAllNodesForLinks();
      this.loadAllLinkRoles();
      this.$bvModal.show("modal-manage-professional");
    },

    loadProfessionalLinks() {
      const api = `${this.$store.state.api}node_professionals?paginated=false&professional_id=${this.manageProfessional.id}&with[]=node&with[]=role`;
      return axios.get(api).then((r) => {
        this.professionalLinks = r.data.data;
      });
    },

    loadAllNodesForLinks() {
      return axios
        .get(`${this.$store.state.api}nodes?paginated=false`)
        .then((r) => {
          this.allNodesForLinks = r.data.data;
        });
    },

    loadAllLinkRoles() {
      return axios
        .get(`${this.$store.state.api}roles?paginated=false&status=1`)
        .then((r) => {
          this.allLinkRoles = r.data.data;
        });
    },

    openAddLink() {
      this.newLink = {
        node_id: null,
        role_id: null,
        starts_at: this.todayIso(),
        ends_at: "",
      };
      this.$bvModal.show("modal-add-link");
    },

    saveLink() {
      if (!this.newLink.node_id) return;
      this.$loading(true);
      this.$http
        .post(`${this.$store.state.api}node_professionals`, {
          node_id: this.newLink.node_id,
          professional_id: this.manageProfessional.id,
          role_id: this.newLink.role_id,
          starts_at: this.newLink.starts_at,
          ends_at: this.newLink.ends_at || null,
        })
        .then(() => {
          this.$message(
            this.$t("professional.manage.successTitle"),
            this.$t("professional.manage.linkCreated"),
            "success"
          );
          this.$loading(false);
          this.loadProfessionalLinks();
        })
        .catch((e) => {
          this.$loading(false);
          this.$message(null, e.response.data, "error");
        });
    },

    openEditLink(link) {
      this.editingLink = {
        id: link.id,
        node_id: link.node_id,
        professional_id: link.professional_id,
        role_id: link.role_id,
        professional_type_id: link.professional_type_id,
        starts_at: link.starts_at ? String(link.starts_at).slice(0, 10) : "",
        ends_at: link.ends_at ? String(link.ends_at).slice(0, 10) : "",
      };
      this.$bvModal.show("modal-edit-professional-link");
    },

    saveEditLink() {
      this.$loading(true);
      this.$http
        .put(`${this.$store.state.api}node_professionals/${this.editingLink.id}`, {
          ...this.editingLink,
          ends_at: this.editingLink.ends_at || null,
        })
        .then(() => {
          this.$message(
            this.$t("professional.manage.successTitle"),
            this.$t("professional.manage.validityUpdated"),
            "success"
          );
          this.$loading(false);
          this.loadProfessionalLinks();
        })
        .catch((e) => {
          this.$loading(false);
          this.$message(null, e.response.data, "error");
        });
    },

    async unlinkNode(link) {
      const name = link.node ? link.node.name : link.node_id;
      const ok = await swal({
        title: this.$t("professional.manage.confirmUnlink", { name }),
        icon: "warning",
        buttons: [this.$t("common.cancel"), this.$t("professional.manage.unlink")],
        dangerMode: true,
      });
      if (!ok) return;
      this.$loading(true);
      this.$http
        .delete(`${this.$store.state.api}node_professionals/${link.id}`)
        .then(() => {
          this.$message(
            this.$t("professional.manage.successTitle"),
            this.$t("professional.manage.unlinked"),
            "success"
          );
          this.$loading(false);
          this.loadProfessionalLinks();
        })
        .catch((e) => {
          this.$loading(false);
          this.$message(null, e.response.data, "error");
        });
    },

    todayIso() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    },

    formatIsoDate(value) {
      if (!value) return "";
      const [ano, mes, dia] = String(value).slice(0, 10).split("-");
      return `${dia}/${mes}/${ano}`;
    },

    linkStatus(link) {
      const today = this.todayIso();
      const starts = link.starts_at ? String(link.starts_at).slice(0, 10) : null;
      const ends = link.ends_at ? String(link.ends_at).slice(0, 10) : null;
      if (starts && today < starts) {
        return { label: this.$t("professional.manage.linkStatus.future"), variant: "info" };
      }
      if (ends && today > ends) {
        return { label: this.$t("professional.manage.linkStatus.ended"), variant: "secondary" };
      }
      return { label: this.$t("professional.manage.linkStatus.active"), variant: "success" };
    },

    makeFormData: function () {
      const self = this;
      let fd = new FormData();

      let fileImport = document.getElementById("fileImport");

      fd.append("fileImport", fileImport.files[0] ? fileImport.files[0] : "");

      return fd;
    },
    importFile() {
      const self = this;
      let api = self.$store.state.api + "import-professionals";

      let fd = self.makeFormData();

      axios
        .post(api, fd)
        .then((response) => {
          self.$message(
            self.$t('professional.messages.successTitle'),
            self.$t('professional.messages.importSuccess'),
            "success"
          );
          self.showMessageImport = true;
          self.$refs.professionalsCrud.$refs.table.refresh();
        })
        .catch((error) => {
          self.$message(
            self.$t('professional.messages.importErrorTitle'),
            self.$t('professional.messages.importError'),
            "error"
          );
        });
    },
    query: function (query) {
      let columns = {
        id: "id",
        name: "name",
        cbo_id: "cbo[cbo_name]",
        status: "status",
      };
      let filters = "";
      $.each(query, function (index, value) {
        filters += columns[index] + "=" + value + "&";
      });
      return filters;
    },
    openInput() {
      document.getElementById("fileImport").click();
    },
  },
};
</script>

<style scoped>
::v-deep .manage-modal {
  border-radius: 14px;
  border: none;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
.manage-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  background: #fff;
  border-bottom: 1px solid #ebe9f1;
}
.manage-modal__avatar {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background: #f0eefd;
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.manage-modal__heading {
  flex: 1;
  min-width: 0;
}
.manage-modal__close {
  background: transparent;
  border: none;
  color: #6e6b7b;
  padding: 4px;
  line-height: 0;
  cursor: pointer;
}
.manage-modal__close:hover {
  color: #5e5873;
}
.manage-modal__body {
  padding: 20px 24px;
  max-height: 60vh;
  overflow-y: auto;
}
.manage-modal__section-title {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: #6e6b7b;
}
.link-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.link-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  transition: box-shadow 0.15s, border-color 0.15s;
}
.link-row:hover {
  border-color: #d8d6de;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}
.link-row__icon {
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 50%;
  background: #f0eefd;
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.link-row__main {
  flex: 1;
  min-width: 0;
}
.link-row__node {
  font-weight: 600;
  font-size: 0.95rem;
}
.link-row__meta {
  font-size: 0.78rem;
  color: #6e6b7b;
  margin-top: 2px;
}
.link-row__actions {
  display: flex;
  flex-shrink: 0;
}
.VuePagination {
  text-align: center;
}
.uploading-image {
  display: flex;
  border-radius: 10px;
  width: 100px;
  height: 100px;
}
</style>